import { useEffect, useState } from 'react';
import { MapPin, Plus, X, Edit2, Trash2, Loader2, RotateCcw, ShoppingBag, Store, Clock } from 'lucide-react';
import { useStoreDeliveryAdmin, createDeliveryPoint, updateDeliveryPoint, deleteDeliveryPoint, createDeliveryArea, updateDeliveryArea, deleteDeliveryArea, useStore, updateStore, useStoreTimeSlots } from '../hooks/useStoreApi';
import { AdminTabEmpty } from './store/admin/AdminTabEmpty';

// Delivery Tab Component
export const DeliveryTab = ({ storeId, storeSlug, onRegisterRefresh, chatId }) => {
    // Unified fetch (points + areas)
    const { deliveryPoints, deliveryAreas, loading, error, refetch } = useStoreDeliveryAdmin(storeId, chatId);

    // UI state
    const [viewMode, setViewMode] = useState('points'); // 'points' | 'areas'
    const [showModal, setShowModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null); // point or area
    const [modalType, setModalType] = useState('point'); // 'point' | 'area'
    const [deletingId, setDeletingId] = useState(null);
    const [actionError, setActionError] = useState(null);

    // Register refresh handler for Alt+R
    useEffect(() => {
        if (onRegisterRefresh) {
            onRegisterRefresh('delivery', async () => {
                await refetch();
            });
        }
    }, [onRegisterRefresh, refetch]);

    const handleDelete = async (item) => {
        if (!storeId || !item) return;
        const isArea = item.delivery_area_id || item.type === 'delivery_area';
        if (!window.confirm(isArea ? 'למחוק אזור משלוח זה?' : 'למחוק נקודת חלוקה זו?')) return;
        try {
            const id = isArea ? (item.delivery_area_id || item.dp_id) : (item.dp_id || item.delivery_point_id);
            setDeletingId(id);
            setActionError(null);
            if (isArea) {
                await deleteDeliveryArea(storeId, id, chatId);
            } else {
                await deleteDeliveryPoint(storeId, id, chatId);
            }
            await refetch();
        } catch (err) {
            setActionError(err.message || 'שגיאה במחיקה');
        } finally {
            setDeletingId(null);
        }
    };

    const openNewModal = (type) => {
        setEditingItem(null);
        setModalType(type);
        setShowModal(true);
    };

    const openEditModal = (item, type) => {
        setEditingItem(item);
        setModalType(type);
        setShowModal(true);
    };

    return (
        // Added top margin to prevent the title row from being hidden behind the tabs bar
        <div className="space-y-6 mt-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="flex flex-col gap-2">
                    <h2 className="text-2xl font-bold text-mekomit-900">חלוקה</h2>
                    <div className="inline-flex overflow-hidden rounded-lg border border-mekomit-300 bg-white shadow-sm w-fit">
                        <button
                            onClick={() => setViewMode('points')}
                            className={`px-4 py-2 text-sm font-medium transition-colors ${viewMode === 'points' ? 'bg-gradient-to-r from-mekomit-500 to-mekomit-400 text-ink-strong' : 'text-mekomit-700 hover:bg-mekomit-50'}`}
                        >נקודות איסוף</button>
                        <button
                            onClick={() => setViewMode('areas')}
                            className={`px-4 py-2 text-sm font-medium transition-colors border-r ${viewMode === 'areas' ? 'bg-gradient-to-r from-mekomit-500 to-mekomit-400 text-ink-strong' : 'text-mekomit-700 hover:bg-mekomit-50'}`}
                        >אזורי משלוח</button>
                    </div>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => openNewModal(viewMode === 'areas' ? 'area' : 'point')}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-mekomit-500 to-mekomit-400 text-ink-strong font-semibold shadow-md border border-mekomit-300 hover:from-mekomit-600 hover:to-mekomit-500 hover:shadow-lg transition-all"
                    >
                        <Plus className="w-4 h-4" />
                        <span>{viewMode === 'areas' ? 'הוסף אזור' : 'הוסף נקודה'}</span>
                    </button>
                    <button
                        onClick={() => { refetch(); }}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white text-mekomit-700 font-medium border border-mekomit-300 hover:bg-mekomit-50 transition-all"
                        title="רענן"
                    >
                        <RotateCcw className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {actionError && (
                <div className="p-3 bg-danger-50 border border-danger-200 rounded-lg text-danger-700 text-sm">
                    {actionError}
                </div>
            )}

            <ServiceOptionsSection storeId={storeId} storeSlug={storeSlug} chatId={chatId} />

            <div className="bg-white rounded-2xl shadow-card p-6 border border-mekomit-100">
                {viewMode === 'points' ? (
                    <ListPoints
                        loading={loading}
                        error={error}
                        points={deliveryPoints}
                        deletingId={deletingId}
                        openEditModal={(p) => openEditModal(p, 'point')}
                        openCreate={() => openNewModal('point')}
                        handleDelete={handleDelete}
                    />
                ) : (
                    <ListAreas
                        loading={loading}
                        error={error}
                        areas={deliveryAreas}
                        deletingId={deletingId}
                        openEditModal={(a) => openEditModal(a, 'area')}
                        openCreate={() => openNewModal('area')}
                        handleDelete={handleDelete}
                    />
                )}
            </div>

            {showModal && (
                modalType === 'point' ? (
                    <DeliveryPointModal
                        storeId={storeId}
                        deliveryPoint={editingItem}
                        onClose={() => { setShowModal(false); setEditingItem(null); }}
                        onSave={async () => {
                            setShowModal(false);
                            setEditingItem(null);
                            await refetch();
                        }}
                        chatId={chatId}
                    />
                ) : (
                    <DeliveryAreaModal
                        storeId={storeId}
                        area={editingItem}
                        onClose={() => { setShowModal(false); setEditingItem(null); }}
                        onSave={async () => {
                            setShowModal(false);
                            setEditingItem(null);
                            await refetch();
                        }}
                        chatId={chatId}
                    />
                )
            )}
        </div>
    );
};

// On-premise service options (takeaway / eat-in) — stored under settings.service_options
const SERVICE_OPTION_META = {
    takeaway: {
        icon: ShoppingBag,
        title: 'איסוף עצמי (Take Away)',
        description: 'הלקוח מזמין מראש ואוסף את ההזמנה מהעסק',
        defaultLabel: 'איסוף עצמי',
    },
    eat_in: {
        icon: Store,
        title: 'שירות במקום העסק',
        description: 'הלקוח מזמין, משלם ונשאר לצרוך במקום — מתאים לכל סוג עסק',
        defaultLabel: 'שירות במקום',
    },
};

const DEFAULT_SERVICE_OPTIONS = {
    takeaway: { enabled: false, use_time_slots: false, label: '' },
    eat_in: { enabled: false, use_time_slots: false, label: '' },
};

const ServiceOptionsSection = ({ storeId, storeSlug, chatId }) => {
    const { data: storeData, loading: storeLoading } = useStore(storeSlug, chatId);
    const { timeSlotsEnabled } = useStoreTimeSlots(storeId, chatId);
    const [options, setOptions] = useState(DEFAULT_SERVICE_OPTIONS);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // Seed local state from store settings once loaded
    useEffect(() => {
        const saved = storeData?.settings?.service_options;
        if (saved) {
            setOptions({
                takeaway: { ...DEFAULT_SERVICE_OPTIONS.takeaway, ...(saved.takeaway || {}) },
                eat_in: { ...DEFAULT_SERVICE_OPTIONS.eat_in, ...(saved.eat_in || {}) },
            });
        }
    }, [storeData]);

    const persist = async (next) => {
        const prev = options;
        setOptions(next);
        setSaving(true);
        setError(null);
        try {
            await updateStore(storeId, { service_options: next }, chatId);
        } catch (err) {
            setOptions(prev);
            setError(err.message || 'שגיאה בשמירת אפשרויות השירות');
        } finally {
            setSaving(false);
        }
    };

    const toggleEnabled = (key) => persist({ ...options, [key]: { ...options[key], enabled: !options[key].enabled } });
    const toggleTimeSlots = (key) => persist({ ...options, [key]: { ...options[key], use_time_slots: !options[key].use_time_slots } });
    // Label edits update locally while typing; persisted on blur
    const setLabel = (key, label) => setOptions(prev => ({ ...prev, [key]: { ...prev[key], label } }));
    const persistLabel = (key) => persist({ ...options, [key]: { ...options[key], label: (options[key].label || '').trim() } });

    return (
        <div className="bg-white rounded-2xl shadow-card p-6 border border-mekomit-100">
            <div className="flex items-center justify-between mb-1">
                <h3 className="text-lg font-bold text-mekomit-900">שירות במקום העסק</h3>
                {saving && <Loader2 className="w-4 h-4 animate-spin text-mekomit-500" />}
            </div>
            <p className="text-sm text-mekomit-600 mb-4">אפשרויות קבלה ללא משלוח — הלקוח בוחר אותן בתהליך ההזמנה. אפשר לשלב אותן יחד עם נקודות איסוף, משלוחים וחלונות זמן.</p>
            {error && <div className="mb-3 p-3 bg-danger-50 border border-danger-200 rounded-lg text-danger-700 text-sm">{error}</div>}
            {storeLoading ? (
                <div className="flex items-center gap-2 py-4 text-mekomit-600 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> טוען...</div>
            ) : (
                <div className="grid gap-4 sm:grid-cols-2">
                    {Object.keys(SERVICE_OPTION_META).map(key => {
                        const meta = SERVICE_OPTION_META[key];
                        const Icon = meta.icon;
                        const opt = options[key];
                        return (
                            <div key={key} className={`rounded-xl border p-4 transition-all ${opt.enabled ? 'border-mekomit-300 bg-gradient-to-br from-mekomit-50 to-white shadow-sm' : 'border-line bg-surface-muted/40'}`}>
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-center gap-2">
                                        <Icon className={`w-5 h-5 ${opt.enabled ? 'text-mekomit-600' : 'text-ink-subtle'}`} />
                                        <div>
                                            <h4 className={`font-semibold ${opt.enabled ? 'text-mekomit-900' : 'text-ink-muted'}`}>{meta.title}</h4>
                                            <p className="text-xs text-ink-muted mt-0.5">{meta.description}</p>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        role="switch"
                                        aria-checked={opt.enabled}
                                        onClick={() => toggleEnabled(key)}
                                        disabled={saving}
                                        className={`relative shrink-0 w-11 h-6 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-mekomit-400 ${opt.enabled ? 'bg-success-500' : 'bg-line-strong'}`}
                                        title={opt.enabled ? 'כבה אפשרות זו' : 'הפעל אפשרות זו'}
                                    >
                                        <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${opt.enabled ? 'right-0.5' : 'right-[22px]'}`} />
                                    </button>
                                </div>
                                {opt.enabled && (
                                    <div className="mt-3 pt-3 border-t border-mekomit-100 space-y-3">
                                        <div>
                                            <label className="block text-xs font-medium text-ink mb-1">השם שיוצג ללקוח</label>
                                            <input
                                                type="text"
                                                value={opt.label || ''}
                                                onChange={(e) => setLabel(key, e.target.value)}
                                                onBlur={() => persistLabel(key)}
                                                maxLength={40}
                                                placeholder={meta.defaultLabel}
                                                className="w-full px-3 py-1.5 text-sm border border-line-strong rounded-lg focus:ring-2 focus:ring-mekomit-500 focus:border-transparent text-ink-strong"
                                            />
                                            <p className="text-[11px] text-ink-subtle mt-1">אפשר להתאים לכל סוג עסק — לדוגמה: "שתייה במקום", "שימוש במקום". ריק = "{meta.defaultLabel}".</p>
                                        </div>
                                        <label className="flex items-center gap-2 text-sm text-ink cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={opt.use_time_slots}
                                                onChange={() => toggleTimeSlots(key)}
                                                disabled={saving}
                                                className="h-4 w-4 rounded border-line-strong text-mekomit-600 focus:ring-mekomit-500"
                                            />
                                            <Clock className="w-4 h-4 text-mekomit-600" />
                                            <span>דרוש תיאום מועד מראש (חלונות זמן)</span>
                                        </label>
                                        <p className="text-xs text-ink-subtle mt-1.5 pr-6">
                                            {opt.use_time_slots
                                                ? (timeSlotsEnabled
                                                    ? 'הלקוח יתבקש לבחור חלון זמן לפני התשלום.'
                                                    : 'שים לב: חלונות הזמן כבויים כרגע — יש להפעיל אותם בלשונית חלונות זמן.')
                                                : 'ללא תיאום מראש — הלקוח פשוט מגיע, משלם ומקבל.'}
                                        </p>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// Delivery Point Modal Component
const DeliveryPointModal = ({ storeId, deliveryPoint, onClose, onSave, chatId }) => {
    const [formData, setFormData] = useState({
        name: deliveryPoint?.name || '',
        address: deliveryPoint?.address || '',
        description: deliveryPoint?.description || '',
        delivery_date: deliveryPoint?.delivery_date || '',
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const dpData = {
                name: formData.name,
                address: formData.address,
            };

            if (formData.description) {
                dpData.description = formData.description;
            }
            if (formData.delivery_date) {
                dpData.delivery_date = formData.delivery_date;
            }

            if (deliveryPoint) {
                await updateDeliveryPoint(storeId, deliveryPoint.dp_id || deliveryPoint.delivery_point_id, dpData, chatId);
            } else {
                await createDeliveryPoint(storeId, dpData, chatId);
            }

            onSave();
        } catch (err) {
            setError(err.message || 'שגיאה בשמירת נקודת החלוקה');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-ink-strong">{deliveryPoint ? 'ערוך נקודת חלוקה' : 'הוסף נקודת חלוקה'}</h3>
                    <button onClick={onClose} className="p-2 hover:bg-surface-muted rounded-lg">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                {error && (
                    <div className="mb-4 p-3 bg-danger-50 border border-danger-100 rounded-lg text-danger-700 text-sm">
                        {error}
                    </div>
                )}
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-ink mb-1">שם הנקודה</label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-warning-500 focus:border-transparent text-ink-strong"
                            placeholder="כיכר העיר"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-ink mb-1">כתובת</label>
                        <input
                            type="text"
                            value={formData.address}
                            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                            className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-warning-500 focus:border-transparent text-ink-strong"
                            placeholder="רחוב הרצל 1, תל אביב"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-ink mb-1">תיאור (אופציונלי)</label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-warning-500 focus:border-transparent text-ink-strong"
                            rows="2"
                            placeholder="פרטים נוספים על נקודת החלוקה"
                        />
                    </div>
                    {/* Date field commented out intentionally */}
                    <div className="flex gap-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-4 py-2 border border-line-strong rounded-lg hover:bg-surface-muted transition-colors text-ink-strong"
                            disabled={loading}
                        >
                            ביטול
                        </button>
                        <button
                            type="submit"
                            className="flex-1 px-4 py-2 bg-warning-500 text-white rounded-lg hover:bg-warning-600 transition-colors disabled:opacity-50"
                            disabled={loading}
                        >
                            {loading ? 'שומר...' : (deliveryPoint ? 'שמור' : 'הוסף')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// List Points Component
const ListPoints = ({ loading, error, points, deletingId, openEditModal, openCreate, handleDelete }) => {
    if (loading) return (
        <div className="flex items-center justify-center py-12 text-mekomit-600"><Loader2 className="w-6 h-6 animate-spin mr-2" />טוען נקודות איסוף...</div>
    );
    if (error) return (
        <div className="p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-800 text-sm">שגיאה בטעינה: {error.message || error.toString()}</div>
    );
    if (!points || points.length === 0) return (
        <AdminTabEmpty
            icon={MapPin}
            tone="brand"
            title="נקודת איסוף — מקום שהלקוחות באים אליו"
            body="אם יש לך חנות פיזית, מטבח, או נקודת חלוקה קבועה — זה המקום להגדיר אותה. הלקוחות יראו את הכתובת ויוכלו לבחור לאסוף משם."
            examples={[
                'הבית או המטבח שלך עם תיאור איך להגיע',
                'נקודת חלוקה ביישוב מסוים, פעם בשבוע',
                'חניון בית כנסת בערב שבת — תאריך וזמן ספציפי',
            ]}
            primaryAction={openCreate ? { label: 'להוסיף נקודת איסוף', onClick: openCreate } : undefined}
        />
    );
    return (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {points.map((dp) => {
                const id = dp.dp_id || dp.delivery_point_id;
                return (
                    <div key={id} className="group relative rounded-xl border border-mekomit-200 bg-gradient-to-br from-mekomit-50 to-white p-4 shadow-sm hover:shadow-md transition-all">
                        <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                                <MapPin className="w-5 h-5 text-mekomit-600" />
                                <h3 className="font-semibold text-mekomit-900 truncate" title={dp.name}>{dp.name}</h3>
                            </div>
                            <div className="flex items-center gap-1">
                                <button onClick={() => openEditModal(dp)} className="p-2 rounded-lg hover:bg-mekomit-100 text-mekomit-700" title="ערוך"><Edit2 className="w-4 h-4" /></button>
                                <button onClick={() => handleDelete(dp)} disabled={deletingId === id} className="p-2 rounded-lg hover:bg-danger-50 text-danger-600 disabled:opacity-50" title="מחק">
                                    {deletingId === id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>
                        <div className="text-sm text-mekomit-700 space-y-1">
                            <p className="font-medium">כתובת: <span className="font-normal">{dp.address}</span></p>
                            {dp.description && <p className="line-clamp-3" title={dp.description}>{dp.description}</p>}
                            {dp.delivery_date && <p className="text-xs text-mekomit-600">תאריך חלוקה: {dp.delivery_date}</p>}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

// List Areas Component
const ListAreas = ({ loading, error, areas, deletingId, openEditModal, openCreate, handleDelete }) => {
    if (loading) return (
        <div className="flex items-center justify-center py-12 text-mekomit-600"><Loader2 className="w-6 h-6 animate-spin mr-2" />טוען אזורי משלוח...</div>
    );
    if (error) return (
        <div className="p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-800 text-sm">שגיאה בטעינה: {error.message || error.toString()}</div>
    );
    if (!areas || areas.length === 0) return (
        <AdminTabEmpty
            icon={MapPin}
            tone="info"
            title="אזורי משלוח — מי מקבל ולמה זה עולה"
            body="הגדרה של אזורי משלוח אומרת ללקוח האם אתה מגיע אליו, וכמה זה עולה. אפשר להוסיף מספר אזורים בבת אחת — פשוט להדביק רשימת שמות."
            examples={[
                'רעננה — ₪25, מינימום ₪80',
                'הוד השרון · כפר סבא — ₪35 (אזורים מרובים בשורה אחת)',
                'יישובי הסביבה — חינם מעל ₪200',
            ]}
            primaryAction={openCreate ? { label: 'להוסיף אזור משלוחים', onClick: openCreate } : undefined}
        />
    );
    return (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {areas.map((a) => {
                const id = a.delivery_area_id || a.dp_id;
                return (
                    <div key={id} className="group relative rounded-xl border border-mekomit-200 bg-gradient-to-br from-mekomit-50 to-white p-4 shadow-sm hover:shadow-md transition-all">
                        <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                                <MapPin className="w-5 h-5 text-mekomit-600" />
                                <h3 className="font-semibold text-mekomit-900 truncate" title={a.name}>{a.name}</h3>
                            </div>
                            <div className="flex items-center gap-1">
                                <button onClick={() => openEditModal(a)} className="p-2 rounded-lg hover:bg-mekomit-100 text-mekomit-700" title="ערוך"><Edit2 className="w-4 h-4" /></button>
                                <button onClick={() => handleDelete(a)} disabled={deletingId === id} className="p-2 rounded-lg hover:bg-danger-50 text-danger-600 disabled:opacity-50" title="מחק">
                                    {deletingId === id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>
                        <div className="text-sm text-mekomit-700 space-y-1">
                            {a.fee != null && <p><span className="font-medium">דמי משלוח:</span> {a.fee}₪</p>}
                            {a.min_order != null && <p><span className="font-medium">מינימום הזמנה:</span> {a.min_order}₪</p>}
                            {a.is_active === false && <p className="text-xs text-danger-600">לא פעיל</p>}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

// Delivery Area Modal
const DeliveryAreaModal = ({ storeId, area, onClose, onSave, chatId }) => {
    const [formData, setFormData] = useState({
        name: area?.name || '',
        fee: area?.fee ?? '',
        min_order: area?.min_order ?? '',
        is_active: area?.is_active ?? true,
    });
    const [bulkNames, setBulkNames] = useState('');
    const [bulkMode, setBulkMode] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            if (area) {
                const updates = { ...formData };
                if (updates.fee === '') updates.fee = null;
                if (updates.min_order === '') updates.min_order = null;
                await updateDeliveryArea(storeId, area.delivery_area_id || area.dp_id, updates, chatId);
            } else if (bulkMode && bulkNames.trim()) {
                const namesArr = bulkNames.split(/[\n,]/).map(n => n.trim()).filter(Boolean);
                await createDeliveryArea(storeId, { area_names: namesArr, fee: formData.fee || null, min_order: formData.min_order || null }, chatId);
            } else {
                const payload = { name: formData.name, fee: formData.fee || null, min_order: formData.min_order || null };
                await createDeliveryArea(storeId, payload, chatId);
            }
            onSave();
        } catch (err) {
            setError(err.message || 'שגיאה בשמירת אזור משלוח');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-ink-strong">{area ? 'ערוך אזור משלוח' : 'הוסף אזור משלוח'}</h3>
                    <button onClick={onClose} className="p-2 hover:bg-surface-muted rounded-lg"><X className="w-5 h-5" /></button>
                </div>
                {error && <div className="mb-4 p-3 bg-danger-50 border border-danger-100 rounded-lg text-danger-700 text-sm">{error}</div>}
                {!area && (
                    <div className="flex items-center gap-2 mb-4">
                        <input id="bulkMode" type="checkbox" className="h-4 w-4" checked={bulkMode} onChange={(e) => setBulkMode(e.target.checked)} />
                        <label htmlFor="bulkMode" className="text-sm text-ink">הוספת רשימת אזורים (מופרדים בפסיק או שורה)</label>
                    </div>
                )}
                <form onSubmit={handleSubmit} className="space-y-4">
                    {!bulkMode && (
                        <div>
                            <label className="block text-sm font-medium text-ink mb-1">שם האזור</label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-warning-500 focus:border-transparent text-ink-strong"
                                placeholder="תל אביב"
                                required={!bulkMode}
                                disabled={bulkMode}
                            />
                        </div>
                    )}
                    {bulkMode && !area && (
                        <div>
                            <label className="block text-sm font-medium text-ink mb-1">שמות אזורים</label>
                            <textarea
                                value={bulkNames}
                                onChange={(e) => setBulkNames(e.target.value)}
                                className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-warning-500 focus:border-transparent text-ink-strong"
                                rows={4}
                                placeholder="תל אביב, ירושלים, חיפה"
                                required
                            />
                        </div>
                    )}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-ink mb-1">דמי משלוח (₪)</label>
                            <input
                                type="number"
                                min="0"
                                value={formData.fee}
                                onChange={(e) => setFormData({ ...formData, fee: e.target.value })}
                                className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-warning-500 focus:border-transparent text-ink-strong"
                                placeholder="לדוגמה: 15"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-ink mb-1">מינימום הזמנה (₪)</label>
                            <input
                                type="number"
                                min="0"
                                value={formData.min_order}
                                onChange={(e) => setFormData({ ...formData, min_order: e.target.value })}
                                className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-warning-500 focus:border-transparent text-ink-strong"
                                placeholder="לדוגמה: 50"
                            />
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <input
                            id="isActive"
                            type="checkbox"
                            checked={formData.is_active}
                            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                            className="h-4 w-4"
                        />
                        <label htmlFor="isActive" className="text-sm text-ink">פעיל</label>
                    </div>
                    <div className="flex gap-3 pt-4">
                        <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-line-strong rounded-lg hover:bg-surface-muted transition-colors text-ink-strong" disabled={loading}>ביטול</button>
                        <button type="submit" className="flex-1 px-4 py-2 bg-warning-500 text-white rounded-lg hover:bg-warning-600 transition-colors disabled:opacity-50" disabled={loading}>{loading ? 'שומר...' : (area ? 'שמור' : 'הוסף')}</button>
                    </div>
                </form>
            </div>
        </div>
    );
};
